import React, { FC, PropsWithChildren, ReactNode, useEffect, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { useSelector } from "react-redux";
import * as _ from "lodash";
import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikConfig } from "formik";
import { useDeepCompareEffect } from "use-deep-compare";
import { PlotEvent } from "@ant-design/charts";

import {
  Breadcrumbs,
  CardEntity,
  Charts,
  ChartTimePeriod,
  DataDescriptorEntity,
  DataDictionary,
  Enterprise,
  EnterpriseAppState,
  EventSourceType,
  ExtendedEventEntity,
  ISODuration,
  MetricEntity,
  V3DisplayFilter
} from "@ctra/api";

import { clampChartUnitList, classname, Nullable, Optional } from "@ctra/utils";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";

import {
  Button,
  Charts as ChartUI,
  Col,
  CtraLayout,
  PlusOutlined,
  Row,
  SwitchWithLabel
} from "@ctra/components";

import {
  Chart,
  ChartAPIContext,
  ChartFilterContext,
  EventList,
  ISODurationFilter,
  useTimePeriod
} from "@chart";

import { useFarm } from "@farms";
import { EventListContext } from "@events";
import { GAEvent } from "@ctra/analytics";
import { useDataDictionary } from "@base";

import { OverlayManager } from "../../chart-layout/v3/OverlayManager";
import { GACategories } from "../../../analytics";
import { InternalisedCard } from "../../../providers";
import { formatFilters, makeDefaultDataFilters } from "../../../utils";
import { CorrelationList } from "../../chart-layout/v3/CorrelationList";

import styles from "./CompareChartsPage.module.less";
import moment from "moment";

const { WidgetWrapper, ContentWrapper } = CtraLayout;
const { ChartVariant } = ChartUI;

const {
  components: { Breadcrumb }
} = Breadcrumbs;

const {
  analytics: {
    correlations: { events },
    v3: {
      config: { cta }
    }
  },
  layouts: {
    dashboard: { compareCharts }
  }
} = Content;

type OverlayCollection = Array<
  Array<{
    metricID: string;
    variantID: string;
  }>
>;

/**
 * Get the initial ISO duration
 * @param {InternalisedCard["dateRange"]} dateRange
 * @param {DataDescriptorEntity} variant
 * @returns {Nullable<ISODuration>}
 */
const resolveIsoDuration = (
  dateRange?: InternalisedCard["dateRange"],
  variant?: DataDescriptorEntity
): Nullable<ISODuration> => {
  if (dateRange) {
    if (_.size(dateRange) === 1) {
      return _.first(dateRange) as ISODuration;
    } else {
      return null;
    }
  } else if (variant) {
    return _.get(variant, ["valueProperties", "defaultInterval"], DataDictionary.DefaultTimePeriod);
  }

  return DataDictionary.DefaultTimePeriod;
};

/**
 * Get the initial time unit
 * @param {InternalisedCard["dateRange"]} dateRange
 * @param {InternalisedCard["bucketSize"]} bucketSize
 * @param {DataDescriptorEntity} variant
 * @returns {ISODuration}
 */
const resolveBucketSize = (
  dateRange?: InternalisedCard["dateRange"],
  bucketSize?: InternalisedCard["bucketSize"],
  variant?: DataDescriptorEntity
): ISODuration => {
  let baseBucketSize = _.defaultTo(bucketSize, DataDictionary.DefaultDataInterval);

  const isoDurationList = clampChartUnitList(
    _.defaultTo(resolveIsoDuration(dateRange, variant), DataDictionary.DefaultTimePeriod)
  );

  if (variant) {
    const defaultBucketSize = _.get(variant, ["valueProperties", "defaultBucketSize"]);

    baseBucketSize = _.get(variant, ["valueProperties", "baseTimeGranularity"], defaultBucketSize);
  } else {
    baseBucketSize = DataDictionary.DefaultDataInterval;
  }

  return _.includes(isoDurationList, baseBucketSize)
    ? baseBucketSize
    : _.defaultTo(_.first(isoDurationList), DataDictionary.DefaultDataInterval);
};

/**
 * Manage the collection of overlays
 * @param {(fieldArrayProps: FieldArrayRenderProps, values: OverlayCollection) => React.ReactNode} renderField
 * @param {any} initialValues
 * @param {Omit<React.PropsWithChildren<{renderField: (fieldArrayProps: FieldArrayRenderProps, values: OverlayCollection) => React.ReactNode} & <Values=FormikValues extends FormikValues, ExtraProps={}>(props: (FormikConfig<Values> & ExtraProps)) => JSX.Element extends React.JSXElementConstructor<infer P> ? P : (<Values=FormikValues extends FormikValues, ExtraProps={}>(props: (FormikConfig<Values> & ExtraProps)) => JSX.Element extends keyof JSX.IntrinsicElements ? JSX.IntrinsicElements[<Values=FormikValues extends FormikValues, ExtraProps={}>(props: (FormikConfig<Values> & ExtraProps)) => JSX.Element] : {})>, "initialValues" | "renderField">} props
 * @returns {JSX.Element}
 * @constructor
 */
const CollectionManager: React.FC<
  {
    render: (fieldArrayProps: FieldArrayRenderProps, values: OverlayCollection) => ReactNode;
  } & Omit<FormikConfig<{ items: OverlayCollection }>, "render">
> = ({ render, initialValues, ...props }) => {
  return (
    <Formik<{ items: OverlayCollection }> initialValues={initialValues} {...props}>
      {({ values }) => (
        <Form>
          <FieldArray name="items">{(fieldArrayProps) => render(fieldArrayProps, values.items)}</FieldArray>
        </Form>
      )}
    </Formik>
  );
};

/**
 * Toolbar for the collection manager
 * @param {(obj: any) => void} push
 * @param {(value: any) => number} unshift
 * @returns {JSX.Element}
 * @constructor
 */
const Toolbar: FC<{ values: OverlayCollection } & FieldArrayRenderProps> = ({ push, pop, children }) => {
  const { t } = useTranslation();

  return (
    <Row gutter={[16, 0]} wrap={false}>
      {children}
      <Col>
        <Button icon={<PlusOutlined />} type={"primary"} onClick={() => push([])}>
          {t<string>(cta.add)}
        </Button>
      </Col>
    </Row>
  );
};

/**
 * Compare charts page with correlation, including initial card handling
 * @returns {JSX.Element}
 * @constructor
 */
export const CompareChartsPage = () => {
  const { t } = useTranslation();
  const { farm: farmContext, site: siteContext } = useFarm();

  const { params } =
    useRouteMatch<{
      source?: "card" | "metric";
      objectID?: CardEntity["id"] | MetricEntity["id"];
    }>();

  /**
   * Set up a ref for graph image conversion
   * @type {React.MutableRefObject<any>}
   */
  const ref = useRef<any>();

  const {
    dataDescriptors,
    meta: { isLoading: isDataDictionaryLoading },
    groups: { projectionMetrics },
    api: { resolveMetricByVariantID, getDefaultVariant }
  } = useDataDictionary();

  const { source, objectID } = params;

  /**
   * Find the card in the state
   * @type {Optional<ReturnType<((state: EnterpriseAppState, ...params: [{id}: {id: CardEntity["id"]}] | [props: Partial<never> | undefined]) => {id: string, layers: any, variantIDs: any[], displayName: string | null | undefined, chartType: ChartType, dateRange: [string] | [string, string], bucketSize: string | undefined, displayFilters: any, dataFilters: any, clientParams: Record<string, unknown>}) & OutputSelectorFields<((...args: SelectorResultArray<[(state: EnterpriseAppState, {id}: {id: CardEntity["id"]}) => CardEntity, (state: EnterpriseAppState, props?: Partial<never>) => LayerCollection]>) => {id: string, layers: any, variantIDs: any[], displayName: string | null | undefined, chartType: ChartType, dateRange: [string] | [string, string], bucketSize: string | undefined, displayFilters: any, dataFilters: any, clientParams: Record<string, unknown>}) & Pick<<F extends (...args: any[]) => any>(func: F, equalityCheckOrOptions?: (EqualityFn | DefaultMemoizeOptions)) => (F & {clearCache: () => void}) extends ((...args: any) => infer R) ? R : any, never>, {id: string, layers: any, variantIDs: any[], displayName: string | null | undefined, chartType: ChartType, dateRange: [string] | [string, string], bucketSize: string | undefined, displayFilters: any, dataFilters: any, clientParams: Record<string, unknown>}> & Pick<<F extends (...args: any[]) => any>(func: F, equalityCheckOrOptions?: (EqualityFn | DefaultMemoizeOptions)) => (F & {clearCache: () => void}) extends ((...args: any) => infer R) ? R : any, never>>>}
   */
  const card = useSelector<EnterpriseAppState, Optional<ReturnType<typeof Enterprise.entities.getCard>>>(
    (state) => {
      if (source === "card" && objectID) {
        return Enterprise.entities.getCard(state, {
          id: objectID
        });
      }
    }
  );

  /**
   * Tell whether we initialize the page from a card or not
   * @type {boolean}
   */
  const fromCard = !!card;

  const clientParams = fromCard ? _.get(card, "clientParams", {}) : {};
  const contextDateRange = fromCard ? card.dateRange : void 0;
  const contextBucketSize = fromCard ? card.bucketSize : void 0;
  const contextVariant = fromCard ? dataDescriptors[card.variantIDs[0]] : void 0;

  /**
   * Tell if the data descriptor supports multi data interval
   */
  const isMultiDataIntervalSupported = contextVariant
    ? _.get(contextVariant, ["flags", "isMultiDataIntervalSupported"])
    : false;

  /**
   * Get the group ISO duration
   */
  const [isoDuration, setISOduration] = useState<Nullable<ISODuration>>(
    resolveIsoDuration(contextDateRange, contextVariant)
  );

  /**
   * Get the group time unit
   */
  const [bucketSize, setBucketSize] = useState<ISODuration>(
    resolveBucketSize(contextDateRange, contextBucketSize, contextVariant)
  );

  const [eventList, setEventList] = useState<ExtendedEventEntity[]>([]);

  /**
   * Tell whether showing the events
   */
  const [showEvents, setShowEvents] = useState<Record<EventSourceType, boolean>>(
    _.mapValues({ ..._.invert(_.toPlainObject(EventSourceType)) }, () => false) as Record<
      EventSourceType,
      boolean
    >
  );

  /**
   * Get the time period for the group
   */
  const [initialTimePeriod] = useTimePeriod({ isoDuration }, _.defaultTo(0, 86400));
  const [startDate, endDate] = fromCard && _.size(card.dateRange) === 2 ? card.dateRange : [];
  const [timePeriod, setTimePeriod] = useState<ChartTimePeriod>(
    startDate && endDate ? { startDate, endDate } : initialTimePeriod
  );

  /**
   * Resolve the series filter from the current card
   * @type {Record<DataDescriptorEntity["id"], V3DisplayFilter>}
   */
  const seriesFilter: Record<DataDescriptorEntity["id"], V3DisplayFilter> = fromCard
    ? (_.omitBy(
        _.mapValues(card.displayFilters, (filters) =>
          _.find(filters, {
            type: "series"
          })
        ),
        _.isUndefined
      ) as Record<DataDescriptorEntity["id"], V3DisplayFilter>)
    : {};

  /**
   * Get the filter values if there is a card
   * @type {Record<DataDescriptorEntity["id"], Array<string>>}
   */
  const seriesValues: Record<DataDescriptorEntity["id"], Array<string>> = _.mapValues(
    seriesFilter,
    (filter) => _.get(filter, "values", [])
  );

  /**
   * Format the data filters if there is a card
   * @type {Record<DataDescriptorEntity["id"], Record<string, unknown>>}
   */
  const dataFilters = fromCard
    ? formatFilters(
        _.merge(
          _.defaultTo(card.dataFilters, {}),
          makeDefaultDataFilters(
            _.map(card.variantIDs, (id) => dataDescriptors[id]),
            siteContext
          )
        )
      )
    : {};

  /**
   * Make an initial item for the collection manager
   * @type {{variantID: any, metricID: string}[]}
   */
  const initialItems = fromCard
    ? _.map(card?.variantIDs, (variantID) => ({
        variantID,
        metricID: resolveMetricByVariantID(variantID).id
      }))
    : objectID
    ? [
        {
          metricID: objectID,
          variantID: getDefaultVariant(objectID).id
        }
      ]
    : null;

  /**
   * Keep track of the chart collection
   */
  const [chartCollection, setChartCollection] =
    useState<Nullable<Array<{ metricID: MetricEntity["id"]; variantID: DataDescriptorEntity["id"] }>>>(
      initialItems
    );

  /**
   * Tell whether the chart is a projection metric
   * @type {boolean}
   */
  const isProjectionMetric = _.some(chartCollection, (item) => _.includes(projectionMetrics, item?.metricID));

  /**
   * Reset the event list when the chart list changes
   */
  useDeepCompareEffect(() => {
    setEventList([]);
  }, [chartCollection]);

  /**
   * If projection metric, add a month to the end date
   */
  useEffect(() => {
    if (isProjectionMetric) {
      initialTimePeriod.endDate = moment(initialTimePeriod.endDate).add(1, "months").toISOString();
      setISOduration(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectionMetric]);

  /**
   * Update the time period when the iso duration changes
   */
  useDeepCompareEffect(() => {
    const bucketSizeList = clampChartUnitList(isoDuration || timePeriod);

    if (!_.includes(bucketSizeList, bucketSize)) {
      setBucketSize(_.first(bucketSizeList) as ISODuration);
    }
  }, [timePeriod, isoDuration, bucketSize]);

  /**
   * Reset the event list when the show events change
   */
  useDeepCompareEffect(() => {
    if (eventList.length > 0 && !_.some(showEvents)) {
      setEventList([]);
    }
  }, [showEvents]);

  if (!initialItems) {
    throw new Error("No card ID or metric ID provided. Cannot initialise the page.");
  }

  return (
    <EventListContext.Provider farmID={farmContext?.id} siteID={siteContext?.id}>
      <Breadcrumb title={t<string>(compareCharts.title)} />
      <CollectionManager
        initialValues={{ items: [initialItems] }}
        validateOnChange
        validateOnBlur
        validate={({ items }) => {
          setChartCollection(_.flatMap(items));

          return {};
        }}
        onSubmit={() => {
          return void 0;
        }}
        render={(fieldArrayProps, values) => {
          const { replace, remove, push } = fieldArrayProps;

          /**
           * Tell whether the event panel should be hidden
           * @type {boolean}
           */
          const isEventPanelHidden = _.isEmpty(eventList) || _.isEmpty(values);

          /**
           * Make a correlation component
           * @type {JSX.Element | null}
           */
          const correlationsComponent = _.size(values) ? (
            <CorrelationList
              isoDuration={isoDuration}
              variantList={_.map(_.flatten(values), "variantID")}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              variantID={_.last(_.flatten(values))!.variantID}
              onAdd={(variantID) => {
                const { id: metricID } = resolveMetricByVariantID(variantID);

                push([{ metricID, variantID }]);
              }}
            />
          ) : null;

          return (
            <WidgetWrapper
              className={styles.WidgetWrapper}
              loading={isDataDictionaryLoading}
              title={t<string>(compareCharts.title)}
              toolbar={
                <Toolbar values={values} {...fieldArrayProps}>
                  {farmContext && (
                    <EventListContext.Consumer>
                      {({ events: eventList }) =>
                        _.size(eventList) > 0 && (
                          <>
                            <Col>
                              <SwitchWithLabel
                                data-gtm-category={GACategories.configurator}
                                data-gtm-action="Toggle aiModel annotations"
                                // inverted the showEvents because of a render delay
                                data-gtm-info={JSON.stringify({ visible: !showEvents })}
                                defaultChecked={showEvents[EventSourceType.aiModel]}
                                onChange={(checked) =>
                                  setShowEvents((current) => ({
                                    ...current,
                                    [EventSourceType.aiModel]: checked
                                  }))
                                }
                                disabled={_.isEmpty(_.flatten(values))}
                                size="small"
                                label={t(events, {
                                  variant: "ai"
                                })}
                              />
                            </Col>
                            <Col>
                              <SwitchWithLabel
                                data-gtm-category={GACategories.configurator}
                                data-gtm-action="Toggle events"
                                // inverted the showEvents because of a render delay
                                data-gtm-info={JSON.stringify({ visible: !showEvents })}
                                defaultChecked={_.every(_.omit(showEvents, EventSourceType.aiModel))}
                                onChange={(checked) =>
                                  setShowEvents(({ AIModel, ...rest }) => ({
                                    AIModel,
                                    ..._.mapValues(rest, () => checked)
                                  }))
                                }
                                disabled={_.isEmpty(_.flatten(values))}
                                size="small"
                                label={t(events, { variant: "Events" })}
                              />
                            </Col>
                          </>
                        )
                      }
                    </EventListContext.Consumer>
                  )}
                  <Col>
                    <ISODurationFilter
                      data-gtm-category={GACategories.configurator}
                      data-gtm-action="Change ISO duration"
                      durations={Charts.utils.supportedDurations}
                      duration={isoDuration}
                      buckets={
                        fromCard &&
                        isMultiDataIntervalSupported &&
                        Charts.utils.hasBucketSizeSupport(card.chartType)
                          ? void 0
                          : []
                      } // default time units, unless no support for multi intervals
                      timePeriod={timePeriod}
                      bucketSize={bucketSize}
                      onDurationChange={setISOduration}
                      onTimePeriodChange={setTimePeriod}
                      onUnitChange={setBucketSize}
                    />
                  </Col>
                </Toolbar>
              }
            >
              <Row gutter={[16, 0]} wrap={false}>
                <Col flex={1}>
                  <ContentWrapper padded={false} className={classname(styles.ContentWrapper)}>
                    {_.map(values, (collection, idx) => {
                      /**
                       * The card may prefill the first values
                       * @type {boolean}
                       */
                      const isPrefilledByCard = fromCard && idx === 0;

                      /**
                       * Make up a provider component for the overlay manager to use the default context until a metric is selected
                       * @type {React.FunctionComponent<ChartAPIProviderProps> | (({children}: React.PropsWithChildren<unknown>) => JSX.Element)}
                       */
                      const ProviderComponent =
                        _.size(collection) > 0
                          ? ChartAPIContext.Provider
                          : ({ children }: PropsWithChildren<unknown>) => <>{children}</>;

                      return (
                        <ChartFilterContext.Provider
                          isoDuration={isoDuration}
                          bucketSize={bucketSize}
                          timePeriod={timePeriod}
                          currentValues={{ isoDuration, timePeriod, bucketSize }}
                          series={isPrefilledByCard ? seriesValues : void 0}
                          filters={isPrefilledByCard ? dataFilters : void 0}
                        >
                          <ProviderComponent
                            metricVariantID={_.map(collection, "variantID")}
                            farmID={farmContext?.id}
                          >
                            <OverlayManager
                              key={idx}
                              initialValues={{ items: values[idx] }}
                              onSubmit={() => void 0}
                              onRemove={
                                idx
                                  ? () => {
                                      remove(idx);
                                    }
                                  : void 0
                              }
                              onChange={({ items }) => {
                                replace(idx, items);
                              }}
                            />
                            {_.size(collection) > 0 && (
                              <ChartAPIContext.Consumer>
                                {({ metricVariants, charts: [{ entity }], meta: { requestURL } }) => {
                                  return (
                                    <Row wrap={false}>
                                      <Col
                                        span={24}
                                        className={classname(
                                          styles.Chart,
                                          idx < _.size(values) - 1 ? styles.BottomBorder : null
                                        )}
                                      >
                                        <Chart
                                          ref={ref}
                                          key={_.join(metricVariants, ",")}
                                          variant={ChartVariant.V3}
                                          viewOptions={{
                                            showEvents,
                                            zoomEnabled: false,
                                            showSupportedViews: true,
                                            showToolbar: false,
                                            showAnnotations: true
                                          }}
                                          config={{
                                            ...clientParams,
                                            onReady: (plot) => {
                                              ref.current = plot;

                                              plot.on("element:click", (event: PlotEvent) => {
                                                const shape = _.get(event, ["data", "shape"]);
                                                const eventList = _.get(event, ["data", "data", "events"]);

                                                GAEvent(
                                                  GACategories.configurator,
                                                  "Open event",
                                                  JSON.stringify({ eventList })
                                                );

                                                if (shape === "farm-event-point") {
                                                  setEventList(() => eventList);
                                                }
                                              });
                                            },
                                            ...(_.size(values) === 1 ? { height: 450 } : {})
                                          }}
                                        />
                                      </Col>
                                    </Row>
                                  );
                                }}
                              </ChartAPIContext.Consumer>
                            )}
                          </ProviderComponent>
                          {idx === _.size(values) - 1 && _.size(collection) > 0 && (
                            <Row>
                              <Col flex={1}>
                                <section className={styles.CorrelationsSection}>
                                  {correlationsComponent}
                                </section>
                              </Col>
                            </Row>
                          )}
                        </ChartFilterContext.Provider>
                      );
                    })}
                  </ContentWrapper>
                </Col>
                {isEventPanelHidden ? null : (
                  <Col span={5}>
                    <EventList
                      variant={
                        _.every(eventList, (event) => event.source.type === EventSourceType.aiModel)
                          ? EventList.variant.insights
                          : EventList.variant.events
                      }
                      data={eventList}
                      handleClose={() => setEventList([])}
                    />
                  </Col>
                )}
              </Row>
            </WidgetWrapper>
          );
        }}
      ></CollectionManager>
    </EventListContext.Provider>
  );
};
